import { Ball, SimulationConfig, RenderLayer, MorphData, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, ShapeType, BallPiece, RhythmicSimulationState } from '@/types';
import { CAMERA } from '@/services/CameraService';
import { drawBackground, drawContainer, drawBall, drawOutsideCollider } from './drawers/SimulationDrawers';
import { drawBlastEvent, drawCollisionEffect } from '@/services/BreakAnimations';
import { GPURenderer } from './GPURenderer';
import { CoreSimulationLogic } from '@/logic/CoreSimulationLogic';
import { BallBreakAnimation } from '@/logic/animations/BallBreakAnimation';
import { ShapeUtils } from '@/logic/physics/ShapeUtils';
import { BackgroundRenderer } from './BackgroundRenderer';
import { ParallaxRenderer } from './ParallaxRenderer';
import { SpectrumRenderer } from './SpectrumRenderer';
import { CustomDrawerRenderer } from './drawers/CustomDrawerRenderer';
import { OffscreenCanvasManager } from '@/services/OffscreenCanvasManager';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

import { HUDRenderer } from './HUDRenderer';
import { COLLISION_ANIMATIONS } from '@/logic/animations/collisions/CollisionAnimationRegistry';

let gpuTrailsCleared = false;

export const SimulationRenderer = {
  draw: (
    canvas: HTMLCanvasElement,
    simCanvas: HTMLCanvasElement | null,
    bgCanvas: HTMLCanvasElement | null,
    webglRenderer: GPURenderer | null,
    balls: Ball[],
    trailCircles: TrailCircle[],
    collisionEvents: CollisionEvent[],
    blastEvents: BlastEvent[],
    persistentSpectrumBoost: Float32Array,
    escapeRings: EscapeRing[],
    initialEscapeRingCount: number,
    config: SimulationConfig,
    hue: number,
    shake: number,
    counterPop: number,
    timeRemaining: number,
    morphState: { active: boolean, from: ShapeType, to: ShapeType, progress: number },
    containerRadius: number,
    rhythmicBreakPieces: BallPiece[],
    zoomLevel: number = 1.0,
    bounceCount: number = 0,
    previews?: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[],
    rhythmicSimulationsState?: RhythmicSimulationState[],
    rhythmicEscapeShieldActive: boolean = false,
    rhythmicEscapeShieldAngle: number = 0
  ) => {
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const simCtx = simCanvas?.getContext('2d');
    const bgCtx = (bgCanvas && !OffscreenCanvasManager.isUsingOffscreenWorker()) ? bgCanvas.getContext('2d') : null;
    const dpr = window.devicePixelRatio || 1;
    
    const now = Date.now();
    const centerX = (canvas.width / dpr) / 2, centerY = (canvas.height / dpr) / 2, globalHue = hue;
    const cam = CAMERA.update(canvas.width / dpr, canvas.height / dpr, balls, config, shake);
    
    if (webglRenderer) webglRenderer.clear();
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    if (simCtx) {
        simCtx.setTransform(1, 0, 0, 1, 0, 0);
        simCtx.scale(dpr, dpr);
        simCtx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    }
    
    if (bgCtx) {
        bgCtx.setTransform(1, 0, 0, 1, 0, 0);
        bgCtx.scale(dpr, dpr);
        if (!OffscreenCanvasManager.isUsingOffscreenWorker()) {
            BackgroundRenderer.draw(bgCtx, config, canvas.width / dpr, canvas.height / dpr, globalHue);
        }
        ParallaxRenderer.draw(bgCtx, config, cam, canvas.width / dpr, canvas.height / dpr, globalHue);
    } else {
        if (!OffscreenCanvasManager.isUsingOffscreenWorker()) {
            BackgroundRenderer.draw(ctx, config, canvas.width / dpr, canvas.height / dpr, globalHue);
        }
        ParallaxRenderer.draw(ctx, config, cam, canvas.width / dpr, canvas.height / dpr, globalHue);
    }
    
    ctx.save(); 
    
    // Total scale combining zoomLevel and camera scale
    const totalScale = zoomLevel * cam.scale;
    
    // Apply transform centered on screen
    ctx.translate(centerX, centerY);
    ctx.scale(totalScale, totalScale);
    // cam.x and cam.y are already relative to (0,0) in physics
    ctx.translate(cam.x, cam.y);

    drawOutsideCollider(ctx, config);

    const morphData: MorphData | undefined = morphState.active ? {
        from: morphState.from,
        to: morphState.to,
        factor: morphState.progress,
        baseRadius: config.containerRadius
    } : {
        from: config.containerShape,
        to: config.containerShape,
        factor: 0,
        baseRadius: config.containerRadius
    };

    const rgbStr = `hsl(${globalHue}, 100%, 50%)`;

    // FX context (prefer simCtx if available)
    const fxCtx = simCtx || ctx;
    
    const renderLayers: Record<RenderLayer, () => void> = {
        WALLS: () => {
            if (config.mode === 'SPACE_SPIRAL') return; 
            if ((config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE') && !config.escapeShowOuterBorders) return;
            
            if (config.mode === 'RHYTHMIC' && config.rhythmicMultiSimEnabled && rhythmicSimulationsState) {
                rhythmicSimulationsState.forEach(sim => {
                    if (!sim.active) return;
                    
                    // Spawn animation
                    const spawnElapsed = now - sim.spawnTime;
                    const spawnDuration = 1000;
                    const spawnProgress = Math.min(1, spawnElapsed / spawnDuration);
                    const spawnScale = 0.5 + 0.5 * Math.sin(spawnProgress * Math.PI / 2);
                    const spawnAlpha = spawnProgress;

                    fxCtx.save();
                    fxCtx.translate(sim.center.x, sim.center.y);
                    fxCtx.scale(spawnScale, spawnScale);
                    fxCtx.globalAlpha *= spawnAlpha;
                    
                    const mergedCfg = { ...config, ...sim.settings };
                    const simMorphData = { ...morphData, baseRadius: sim.containerRadius };
                    drawContainer(fxCtx, 0, 0, mergedCfg, globalHue, sim.containerRadius, simMorphData);
                    PerformanceTelemetry.incrementDrawCalls('canvas', 1);
                    
                    fxCtx.restore();
                });
            } else {
                if (webglRenderer) {
                    const morphData = morphState.active ? {
                        from: morphState.from,
                        to: morphState.to,
                        factor: morphState.progress
                    } : {
                        from: config.containerShape,
                        to: config.containerShape,
                        factor: 0
                    };
                    webglRenderer.renderContainer(config, cam, morphData, containerRadius, globalHue, zoomLevel);
                    PerformanceTelemetry.incrementDrawCalls('webgl', 1);
                } else {
                    drawContainer(fxCtx, 0, 0, config, globalHue, containerRadius, morphData);
                    PerformanceTelemetry.incrementDrawCalls('canvas', 1);
                }
            }
        },
        RINGS: () => {
            if (config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE') {
                if (config.mode === 'RHYTHMIC_ESCAPE' && rhythmicEscapeShieldActive) {
                    const radius = config.rhythmicEscapeShieldRingRadius;
                    const lw = config.escapeRingThickness || 4;
                    
                    fxCtx.save();
                    fxCtx.beginPath();
                    fxCtx.strokeStyle = `hsla(${globalHue}, 100%, 70%, 1.0)`;
                    fxCtx.lineWidth = lw;
                    
                    if (morphData.factor > 0 || morphData.from !== 'CIRCLE') {
                        // Drawing complex shape shield with rotation
                        for (let a = 0; a <= Math.PI * 2 + 0.1; a += 0.1) {
                            const r = ShapeUtils.getInterpolatedRadiusAtAngle(a - rhythmicEscapeShieldAngle, morphData.from, morphData.to, morphData.factor, radius, morphData.baseRadius);
                            const px = Math.cos(a) * r;
                            const py = Math.sin(a) * r;
                            if (a === 0) fxCtx.moveTo(px, py);
                            else fxCtx.lineTo(px, py);
                        }
                    } else {
                        fxCtx.arc(0, 0, radius, 0, Math.PI * 2);
                    }
                    fxCtx.stroke();
                    fxCtx.restore();
                    PerformanceTelemetry.incrementDrawCalls('canvas', 1);
                }
                CoreSimulationLogic.EscapeLogic.render(fxCtx, 0, 0, escapeRings, config, globalHue, morphData, webglRenderer, cam, initialEscapeRingCount, timeRemaining, balls, counterPop, zoomLevel);
                PerformanceTelemetry.incrementDrawCalls(webglRenderer ? 'webgl' : 'canvas', webglRenderer ? 1 : Math.max(1, escapeRings.length));
            }
        },
        TRAIL: () => {
            const trailsActive = config.enableTrails || config.trailEnabled;
            if (trailsActive) {
                gpuTrailsCleared = false;
                if (webglRenderer && config.gpuPingPongTrailsEnabled !== false) {
                    webglRenderer.prepareGPUPingPongTrails(config);
                    webglRenderer.renderBallsToTrails(balls, config, cam, zoomLevel);
                    webglRenderer.drawGPUTrailsToScreen();
                    PerformanceTelemetry.incrementDrawCalls('webgl', 3);
                    
                    if (config.enableDrawer) {
                        CustomDrawerRenderer.render(fxCtx, balls, config, globalHue);
                        PerformanceTelemetry.incrementDrawCalls('canvas', balls.length);
                    }
                } else {
                    const useWebGL = webglRenderer && 
                                     (config.ballShape === 'CIRCLE' || !config.ballShape) && 
                                     (config.ballTexture === 'NONE' || !config.ballTexture);
                    
                    // Draw Drawer Lines
                    if (config.enableDrawer) {
                        CustomDrawerRenderer.render(fxCtx, balls, config, globalHue);
                        PerformanceTelemetry.incrementDrawCalls('canvas', balls.length);
                    }

                    CoreSimulationLogic.BasicLogic.renderTrail(fxCtx, trailCircles, config, useWebGL ? webglRenderer : null, cam, globalHue, balls, zoomLevel);
                    PerformanceTelemetry.incrementDrawCalls(useWebGL ? 'webgl' : 'canvas', useWebGL ? 1 : Math.max(1, trailCircles.length / 50));
                }
            } else {
                if (webglRenderer && !gpuTrailsCleared) {
                    webglRenderer.resetGPUPasses();
                    gpuTrailsCleared = true;
                }
                if (config.enableDrawer) {
                    CustomDrawerRenderer.render(fxCtx, balls, config, globalHue);
                    PerformanceTelemetry.incrementDrawCalls('canvas', balls.length);
                }
            }
        },
        BALL: () => {
            if (previews && previews.length > 0) {
                if (webglRenderer) {
                    webglRenderer.renderBallPreviews(previews, config, cam, zoomLevel);
                    PerformanceTelemetry.incrementDrawCalls('webgl', 1);
                } else {
                    PerformanceTelemetry.incrementDrawCalls('canvas', previews.length);
                    previews.forEach(p => {
                        // Draw ball preview
                        fxCtx.save();
                        fxCtx.beginPath();
                        fxCtx.globalAlpha = 0.5;
                        fxCtx.fillStyle = p.color;
                        fxCtx.arc(p.pos.x, p.pos.y, p.radius, 0, Math.PI * 2);
                        fxCtx.fill();
                        
                        // Draw direction arrow
                        const speed = Math.sqrt(p.vel.x * p.vel.x + p.vel.y * p.vel.y);
                        if (speed > 0.1) {
                            const arrowLen = 30;
                            const angle = Math.atan2(p.vel.y, p.vel.x);
                            const endX = p.pos.x + Math.cos(angle) * arrowLen;
                            const endY = p.pos.y + Math.sin(angle) * arrowLen;
                            
                            fxCtx.beginPath();
                            fxCtx.strokeStyle = p.color;
                            fxCtx.lineWidth = 2;
                            fxCtx.moveTo(p.pos.x, p.pos.y);
                            fxCtx.lineTo(endX, endY);
                            
                            // Arrow head
                            const headLen = 8;
                            fxCtx.lineTo(endX - headLen * Math.cos(angle - Math.PI / 6), endY - headLen * Math.sin(angle - Math.PI / 6));
                            fxCtx.moveTo(endX, endY);
                            fxCtx.lineTo(endX - headLen * Math.cos(angle + Math.PI / 6), endY - headLen * Math.sin(angle + Math.PI / 6));
                            fxCtx.stroke();
                        }
                        fxCtx.restore();
                    });
                }
            }
            
            if (config.mode === 'SPACE_SPIRAL') {
                CoreSimulationLogic.SpaceSpiralLogic.render(fxCtx, balls, config, rgbStr, drawBall);
                PerformanceTelemetry.incrementDrawCalls('canvas', balls.length);
            } else if (config.mode === 'RHYTHMIC' && config.rhythmicMultiSimEnabled && rhythmicSimulationsState) {
                let draws = 0;
                balls.forEach(b => {
                    if (b.isBreaking) return;
                    
                    const sim = rhythmicSimulationsState.find(s => s.ball?.id === b.id);
                    if (sim) {
                        if (!sim.active) return; // HIDDEN when inactive

                        const spawnElapsed = now - sim.spawnTime;
                        const spawnDuration = 1000;
                        const spawnProgress = Math.min(1, spawnElapsed / spawnDuration);
                        const spawnScale = 0.5 + 0.5 * Math.sin(spawnProgress * Math.PI / 2);
                        const spawnAlpha = spawnProgress;

                        fxCtx.save();
                        fxCtx.translate(sim.center.x, sim.center.y);
                        fxCtx.scale(spawnScale, spawnScale);
                        fxCtx.translate(-sim.center.x, -sim.center.y);
                        fxCtx.globalAlpha *= spawnAlpha;
                        const mergedCfg = { ...config, ...sim.settings };
                        drawBall(fxCtx, b, mergedCfg, b.color);
                        fxCtx.restore();
                    } else {
                        drawBall(fxCtx, b, config, b.color);
                    }
                    draws++;
                });
                PerformanceTelemetry.incrementDrawCalls('canvas', draws);
            } else {
                const useWebGL = webglRenderer && 
                                 (config.ballShape === 'CIRCLE' || !config.ballShape) && 
                                 (config.ballTexture === 'NONE' || !config.ballTexture);

                if (useWebGL && webglRenderer) {
                    webglRenderer.render(balls, config, cam, zoomLevel);
                    PerformanceTelemetry.incrementDrawCalls('webgl', 1);
                } else {
                    const margin = config.frustumCullingMargin || 100;
                    const left = -centerX / totalScale - cam.x - margin;
                    const right = centerX / totalScale - cam.x + margin;
                    const top = -centerY / totalScale - cam.y - margin;
                    const bottom = centerY / totalScale - cam.y + margin;

                    const ballLimit = config.enableHyperSpeed && config.mode === 'ESCAPE' ? (config.hyperSpeedRenderLimit || 100) : Infinity;
                    let drawnCount = 0;

                    for (let i = 0; i < balls.length; i++) {
                        const b = balls[i];
                        if (b.isBreaking) continue;
                        if (drawnCount >= ballLimit) break;
                        
                        // Frustum Culling
                        if (config.enableFrustumCulling) {
                            if (b.pos.x < left - b.radius || b.pos.x > right + b.radius || 
                                b.pos.y < top - b.radius || b.pos.y > bottom + b.radius) {
                                continue;
                            }
                        }
                        
                        drawBall(fxCtx, b, config, b.color);
                        drawnCount++;
                    }
                    PerformanceTelemetry.incrementDrawCalls('canvas', drawnCount);
                }
            }

            // Draw Rhythmic Break Pieces
            if (rhythmicBreakPieces.length > 0) {
                BallBreakAnimation.drawPieces(fxCtx, rhythmicBreakPieces, now);
                PerformanceTelemetry.incrementDrawCalls('canvas', rhythmicBreakPieces.length);
            }
        },
        FX: () => {
            const effectType = config.collisionEffect || 'RIPPLE';
            if (effectType === 'NONE') {
                return;
            }

            if (COLLISION_ANIMATIONS[effectType] !== undefined) {
                collisionEvents.forEach(e => {
                    drawCollisionEffect(fxCtx, e, config, globalHue, canvas.width / dpr, canvas.height / dpr, cam);
                });
                PerformanceTelemetry.incrementDrawCalls('canvas', collisionEvents.length);
            } else if (webglRenderer && config.gpuParticlesEnabled !== false) {
                webglRenderer.updateCollisionEvents(collisionEvents, config);
                webglRenderer.renderGPUParticles(config, cam);
                PerformanceTelemetry.incrementDrawCalls('webgl', 2);
            }
        },
        BLAST: () => {
            // Force Canvas for blasts to support complex animations like SHATTER
            blastEvents.forEach(e => drawBlastEvent(fxCtx, e, config, globalHue, canvas.width / dpr, canvas.height / dpr, cam)); 
            PerformanceTelemetry.incrementDrawCalls('canvas', blastEvents.length);
        },
        SPECTRUM: () => {
            SpectrumRenderer.render(fxCtx, persistentSpectrumBoost, config, globalHue, morphData);
            PerformanceTelemetry.incrementDrawCalls('canvas', 1);
        },
        HUD: () => {
            HUDRenderer.render(
                ctx,
                0,
                0,
                canvas,
                dpr,
                balls,
                escapeRings,
                initialEscapeRingCount,
                config,
                globalHue,
                counterPop,
                timeRemaining,
                config.mode === 'RHYTHMIC_ESCAPE' ? (config.rhythmicEscapeShieldRingRadius || 100) : containerRadius,
                bounceCount
            );
            PerformanceTelemetry.incrementDrawCalls('canvas', 1);
        }
    };

    const defaultOrder: RenderLayer[] = ['WALLS', 'SPECTRUM', 'RINGS', 'TRAIL', 'BLAST', 'FX', 'BALL', 'HUD'];
    const order = config.renderOrder || defaultOrder;
    
    // Ensure SPECTRUM is in the order if it's missing (for existing configs)
    const finalOrder = (order.includes('SPECTRUM') ? order : ['WALLS', 'SPECTRUM', ...order.filter(l => l !== 'WALLS' && l !== 'SPECTRUM')]) as RenderLayer[];

    if (simCtx) {
        simCtx.save();
        simCtx.translate(centerX, centerY);
        simCtx.scale(totalScale, totalScale);
        simCtx.translate(cam.x, cam.y);
    }
    
    finalOrder.forEach((layer: RenderLayer) => {
        if (renderLayers[layer]) renderLayers[layer]();
    });

    if (simCtx) simCtx.restore();
    ctx.restore();
  }
};
